// src/pages/admin/tabs/MobileAllQuotationsTab.jsx
// Touch-optimized card list of quotations for the mobile admin dashboard.
// Reuses the same filter state + actions from the shared useQuotations hook.

import { useState } from "react";
import { B, fonts, Btn } from "../../../components/UI";
import CustomerDetailPage from "./CustomerDetailPage";

const formatDateFilterDisplay = (dateValue) => {
  if (!dateValue) return "";
  const [year, month, day] = dateValue.split("-");
  if (!year || !month || !day) return "";
  return `${day}/${month}/${year}`;
};

const getDateFilterMessage = (fromDisplay, toDisplay) => {
  if (!fromDisplay && !toDisplay) return "Showing quotations for all dates.";
  const parts = [];
  if (fromDisplay) parts.push(`from ${fromDisplay}`);
  if (toDisplay) parts.push(`to ${toDisplay}`);
  return `Showing quotations ${parts.join(" ")}.`;
};

export default function MobileAllQuotationsTab({
  quotations,
  customers,
  designs,
  filteredQuotations,
  quotationSearch,
  setQuotationSearch,
  quotationCustomerFilter,
  setQuotationCustomerFilter,
  quotationTypeFilter,
  setQuotationTypeFilter,
  quotationDateFrom,
  setQuotationDateFrom,
  quotationDateTo,
  setQuotationDateTo,
  setViewQuotation,
  viewQuotation,
  setEditQuotation,
  setTab,
  handleSaveQuotation,
  quotationSaving,
  handleDeleteQuotation,
  canManageQuotations,
  handlePrintQuotation,
  handlePrintTransportSlip,
  handleSendWhatsApp,
  handleSendUpiQr,
  formatDateTime,
  formatCurrency,
  quotationPage,
  quotationTotalPages,
  setQuotationPage,
  QuotationBuilder,
  quotationSettings,
}) {
  const [expandedId, setExpandedId] = useState(null);
  const [viewCustomer, setViewCustomer] = useState(null);

  const showFirmName = Boolean(quotationSettings?.showFirmName);

  const filteredTotals = filteredQuotations.reduce(
    (acc, quotation) => {
      acc.pcs += (quotation.items || []).reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
      const amount = Number(quotation.grand_total) || 0;
      acc.amount += amount;
      if (quotation.bill_number) {
        acc.billAmount += amount;
      } else {
        acc.quotationAmount += amount;
      }
      return acc;
    },
    { pcs: 0, amount: 0, billAmount: 0, quotationAmount: 0 }
  );

  const dateFilterFromDisplay = formatDateFilterDisplay(quotationDateFrom);
  const dateFilterToDisplay = formatDateFilterDisplay(quotationDateTo);
  const dateFilterMessage = getDateFilterMessage(dateFilterFromDisplay, dateFilterToDisplay);

  const startQuote = () => { setEditQuotation(null); setTab("create"); };

  const handleEdit = (quotation) => {
    if (!canManageQuotations) return;
    setEditQuotation(quotation);
    setTab("create");
  };

  const handleView = (quotation) => setViewQuotation(quotation);

  const mobileInputStyle = {
    width: "100%", background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 12,
    padding: "12px 14px", fontSize: 14, color: B.text, fontFamily: fonts.body, outline: "none",
    boxSizing: "border-box", marginBottom: 10,
  };

  const quotesOnScreen = quotationPage === quotationTotalPages
    ? filteredQuotations.slice((quotationPage - 1) * 10)
    : filteredQuotations.slice((quotationPage - 1) * 10, quotationPage * 10);

  return (
    <div style={{ padding: "16px 16px 110px" }}>
      {/* Header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div>
          <p style={{ margin: 0, fontSize: 13, color: B.textLight, fontFamily: fonts.body }}>{quotations.length} total quotations</p>
          <p style={{ margin: "2px 0 0", fontSize: 12, color: B.textMid, fontWeight: 600, fontFamily: fonts.body }}>{dateFilterMessage}</p>
        </div>
        {canManageQuotations && (
          <button
            onClick={startQuote}
            style={{
              background: B.orange, color: B.white, border: "none", borderRadius: 12,
              padding: "12px 18px", fontSize: 14, fontWeight: 700, cursor: "pointer",
              fontFamily: fonts.body, boxShadow: `0 4px 16px ${B.orange}55`,
            }}
          >
            + New Quotation
          </button>
        )}
      </div>

      {/* Filters */}
      <input
        value={quotationSearch}
        onChange={e => setQuotationSearch(e.target.value)}
        placeholder="Search quotation no or customer..."
        style={mobileInputStyle}
      />
      <div style={{ display: "flex", gap: 8, marginBottom: 4 }}>
        <select
          value={quotationCustomerFilter}
          onChange={e => setQuotationCustomerFilter(e.target.value)}
          style={{ ...mobileInputStyle, flex: 1, marginBottom: 0 }}
        >
          <option value="all">All customers</option>
          {customers.filter(c => c.status !== "deleted").map(customer => (
            <option key={customer.id} value={customer.id}>{customer.customer_name}</option>
          ))}
        </select>
        <select
          value={quotationTypeFilter}
          onChange={e => setQuotationTypeFilter(e.target.value)}
          style={{ ...mobileInputStyle, flex: 1, marginBottom: 0 }}
        >
          <option value="all">All types</option>
          <option value="bills">Bills only</option>
          <option value="quotations">Quotations only</option>
        </select>
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
          <span style={{ fontSize: 11, color: B.textMid, fontFamily: fonts.body }}>From</span>
          <input
            type="date"
            value={quotationDateFrom}
            onChange={e => setQuotationDateFrom(e.target.value)}
            aria-label="Filter quotations from date"
            style={{ ...mobileInputStyle, flex: 1, marginBottom: 0, fontSize: 13 }}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
          <span style={{ fontSize: 11, color: B.textMid, fontFamily: fonts.body }}>To</span>
          <input
            type="date"
            value={quotationDateTo}
            onChange={e => setQuotationDateTo(e.target.value)}
            aria-label="Filter quotations to date"
            style={{ ...mobileInputStyle, flex: 1, marginBottom: 0, fontSize: 13 }}
          />
        </div>
        <span style={{ fontSize: 11, color: B.textLight, fontFamily: fonts.body, whiteSpace: "nowrap" }}>
          {!dateFilterFromDisplay && !dateFilterToDisplay ? "All dates" : ""}
        </span>
      </div>

      {/* Quotation cards */}
      {quotesOnScreen.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 16px", color: B.textLight, fontFamily: fonts.body }}>
          No quotations found.
        </div>
      ) : quotesOnScreen.map(quotation => {
        const pcs = (quotation.items || []).reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
        const isBill = Boolean(quotation.bill_number);
        const isExpanded = expandedId === quotation.id;
        return (
          <div
            key={quotation.id}
            style={{
              background: B.white, border: `1px solid ${B.borderLight}`, borderRadius: 14,
              padding: 14, marginBottom: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <strong style={{ fontSize: 15, color: B.text, fontFamily: fonts.body }}>{quotation.quotation_number || "—"}</strong>
                  {isBill ? (
                    <span style={{ background: B.green + "18", color: B.green, fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, fontFamily: fonts.body }}>
                      Bill
                    </span>
                  ) : (
                    <span style={{ background: B.orange + "18", color: B.orange, fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, fontFamily: fonts.body }}>
                      Quotation
                    </span>
                  )}
                  {quotation.bill_number && (
                    <span style={{ fontSize: 12, color: B.textMid, fontFamily: fonts.body }}>#{quotation.bill_number}</span>
                  )}
                </div>
                <div style={{ fontSize: 14, color: B.text, fontWeight: 600, marginTop: 6, fontFamily: fonts.body }}>
                  {(() => {
                    const customer = customers.find(c => c.id === quotation.customer_id);
                    if (!customer) return quotation.customer_name || "—";
                    return (
                      <button
                        type="button"
                        onClick={() => setViewCustomer(customer)}
                        title="View customer details"
                        style={{
                          border: "none", background: "transparent", cursor: "pointer",
                          fontFamily: fonts.body, fontSize: 14, padding: 0, fontWeight: 600,
                          color: B.text, textDecoration: "underline",
                        }}
                      >
                        {quotation.customer_name || "—"}
                      </button>
                    );
                  })()}
                </div>
                <div style={{ fontSize: 12, color: B.textLight, marginTop: 3, fontFamily: fonts.body }}>
                  {formatDateTime(quotation.created_at)}
                </div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: B.text, fontFamily: fonts.adminDisplay }}>
                  {formatCurrency(quotation.grand_total)}
                </div>
                <div style={{ fontSize: 12, color: B.textMid, fontFamily: fonts.body }}>{pcs} pcs</div>
              </div>
            </div>

            {/* Action row */}
            <div style={{ display: "flex", gap: 10, marginTop: 12, borderTop: `1px solid ${B.borderLight}`, paddingTop: 10 }}>
              <button onClick={() => handleView(quotation)} style={{ flex: 1, background: B.orangeFaint, border: `1px solid ${B.border}`, borderRadius: 8, padding: "9px 0", fontSize: 12, fontWeight: 600, color: B.orange, cursor: "pointer", fontFamily: fonts.body }}>👁️ View</button>
              <button onClick={() => handleEdit(quotation)} disabled={!canManageQuotations} style={{ flex: 1, background: B.orangeFaint, border: `1px solid ${B.border}`, borderRadius: 8, padding: "9px 0", fontSize: 12, fontWeight: 600, color: B.orange, cursor: canManageQuotations ? "pointer" : "not-allowed", opacity: canManageQuotations ? 1 : 0.4, fontFamily: fonts.body }}>✏️ Edit</button>
              <button onClick={() => setExpandedId(isExpanded ? null : quotation.id)} style={{ flex: 1, background: B.orangeFaint, border: `1px solid ${B.border}`, borderRadius: 8, padding: "9px 0", fontSize: 12, fontWeight: 600, color: B.orange, cursor: "pointer", fontFamily: fonts.body }}>⋯ More</button>
            </div>

            {isExpanded && (
              <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                <button onClick={() => handleDeleteQuotation(quotation)} disabled={!canManageQuotations} style={{ flex: 1, background: B.redLight, border: `1px solid ${B.red}33`, borderRadius: 8, padding: "10px 0", fontSize: 12, fontWeight: 700, color: B.red, cursor: canManageQuotations ? "pointer" : "not-allowed", opacity: canManageQuotations ? 1 : 0.4, fontFamily: fonts.body }}>🗑️ Delete</button>
                <button onClick={() => handlePrintQuotation(quotation)} style={{ flex: 1, background: B.orangeFaint, border: `1px solid ${B.border}`, borderRadius: 8, padding: "10px 0", fontSize: 12, fontWeight: 700, color: B.orange, cursor: "pointer", fontFamily: fonts.body }}>🖨️ Print</button>
                <button onClick={() => handlePrintTransportSlip(quotation)} disabled={!String(quotation.transport_name || "").trim()} aria-label="Print transport slip" style={{ flex: 1, background: B.orangeFaint, border: `1px solid ${B.border}`, borderRadius: 8, padding: "10px 0", fontSize: 12, fontWeight: 700, color: B.orange, cursor: quotation.transport_name ? "pointer" : "not-allowed", opacity: quotation.transport_name ? 1 : 0.4, fontFamily: fonts.body }}>📦 Slip</button>
                <button onClick={() => handleSendWhatsApp(quotation)} disabled={!quotation.customer_mobile} style={{ flex: 1, background: "#25D36618", border: `1px solid #25D36644`, borderRadius: 8, padding: "10px 0", fontSize: 12, fontWeight: 700, color: "#1DA851", cursor: quotation.customer_mobile ? "pointer" : "not-allowed", opacity: quotation.customer_mobile ? 1 : 0.4, fontFamily: fonts.body }}>WhatsApp</button>
                {Boolean(quotation.bill_number) && (
                  <button onClick={() => handleSendUpiQr(quotation)} disabled={!quotation.customer_mobile} style={{ flex: 1, background: "#5c3a3112", border: `1px solid #5c3a3144`, borderRadius: 8, padding: "10px 0", fontSize: 12, fontWeight: 700, color: "#5c3a31", cursor: quotation.customer_mobile ? "pointer" : "not-allowed", opacity: quotation.customer_mobile ? 1 : 0.4, fontFamily: fonts.body }}>🔳 UPI QR</button>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Pagination */}
      {filteredQuotations.length > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, gap: 12 }}>
          <Btn onClick={() => setQuotationPage(p => Math.max(1, p - 1))} variant="ghost" small disabled={quotationPage === 1}>Previous</Btn>
          <span style={{ fontSize: 12, color: B.textMid, fontFamily: fonts.body }}>Page {quotationPage} / {quotationTotalPages}</span>
          <Btn onClick={() => setQuotationPage(p => Math.min(quotationTotalPages, p + 1))} variant="ghost" small disabled={quotationPage === quotationTotalPages}>Next</Btn>
        </div>
      )}

      {/* Totals */}
      {filteredQuotations.length > 0 && (
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 6, background: B.orangeFaint, border: `1px solid ${B.borderLight}`, borderRadius: 12, padding: "12px 14px" }}>
          <span style={{ fontSize: 13, color: B.textMid, fontFamily: fonts.body }}>
            Total Pcs: <strong style={{ color: B.text }}>{filteredTotals.pcs}</strong>
          </span>
          <span style={{ fontSize: 13, color: B.textMid, fontFamily: fonts.body }}>
            Bill Amount: <strong style={{ color: B.text }}>{formatCurrency(filteredTotals.billAmount)}</strong>
          </span>
          <span style={{ fontSize: 13, color: B.textMid, fontFamily: fonts.body }}>
            Quotation Amount: <strong style={{ color: B.text }}>{formatCurrency(filteredTotals.quotationAmount)}</strong>
          </span>
        </div>
      )}

      {viewQuotation && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="View quotation"
          style={{
            position: "fixed", inset: 0, zIndex: 600,
            background: B.cream, overflowY: "auto", WebkitOverflowScrolling: "touch",
            paddingBottom: 24,
          }}
        >
          <QuotationBuilder
            customers={customers}
            designs={designs}
            onSave={handleSaveQuotation}
            saving={quotationSaving}
            initialQuotation={viewQuotation}
            onCancelEdit={() => setViewQuotation(null)}
            onBack={() => setViewQuotation(null)}
            readOnly
            quotationSettings={quotationSettings}
            quotations={quotations}
          />
        </div>
      )}

      {viewCustomer && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Customer details"
          style={{
            position: "fixed", inset: 0, zIndex: 650,
            background: B.cream, overflowY: "auto", WebkitOverflowScrolling: "touch",
            paddingBottom: 24,
          }}
        >
          <CustomerDetailPage
            customer={viewCustomer}
            designs={designs}
            onBack={() => setViewCustomer(null)}
            showFirmName={showFirmName}
            backLabel="← Back to Quotations"
          />
        </div>
      )}
    </div>
  );
}
